'use client';

import { getResourceData } from '@/api/public';
import Waterfall from '@/components/Waterfall';
import { useTranslations } from 'next-intl';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTrendEyeglassesTracking } from '../hooks/useTrendEyeglassesTracking';
import { RecommendListData, RecommendProductItem } from '../types';
import TrendProductItemPC from './TrendProductItemPC';
import TrendProductItemMobile from './TrendProductItemMobile';

interface TrendProductListProps {
  deviceType: EPlatform;
  recommendList: RecommendListData;
  // isInApp?: boolean;
  onLoadComplete?: (isComplete: boolean) => void;
}

export default function TrendProductList({
  deviceType,
  recommendList,
  // isInApp = false,
  onLoadComplete,
}: TrendProductListProps) {
  const t = useTranslations('activity.springSale');
  const isWap = deviceType !== EPlatform.WEB;
  const { trackProductClick, trackProductExposure } = useTrendEyeglassesTracking();

  // 分页状态
  const PAGE_SIZE = 20; // 每页12个商品
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [productList, setProductList] = useState<RecommendProductItem[]>(recommendList || []);
  const [hasMore, setHasMore] = useState(true);

  // 瀑布流容器宽度计算
  const containerRef = useRef<HTMLDivElement>(null);
  const [waterfallItemWidth, setWaterfallItemWidth] = useState<number>();

  // 计算瀑布流 item 宽度
  useEffect(() => {
    if (!isWap || !containerRef.current) return;

    const calculateItemWidth = () => {
      const containerWidth = containerRef.current?.offsetWidth || 0;
      const gap = 8;
      const columns = 2;
      // itemWidth = (容器宽度 - gap间距 * (列数 - 1)) / 列数
      const itemWidth = Math.floor((containerWidth - gap * (columns - 1)) / columns);
      setWaterfallItemWidth(itemWidth);
    };

    // 初始计算
    calculateItemWidth();

    // 监听窗口大小变化
    window.addEventListener('resize', calculateItemWidth);
    return () => window.removeEventListener('resize', calculateItemWidth);
  }, [isWap]);

  // 加载更多检测器
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // 监听 hasMore 变化，当没有更多数据时通知父组件加载完成
  useEffect(() => {
    if (!hasMore && productList.length > 0) {
      onLoadComplete?.(true);
    }
  }, [hasMore, productList.length, onLoadComplete]);

  // 加载下一页数据
  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const { code, data } = await getResourceData({
        special_page: {
          key: 'spring_sale_trend_page',
          type: 1,
          limit: PAGE_SIZE,
          page: currentPage + 1,
        },
      });

      if (code !== 200) {
        setHasMore(false);
        return;
      }

      if (data?.special_page?.product_list && data.special_page.product_list.length > 0) {
        const newProducts = data.special_page.product_list;
        setProductList((prev) => [...prev, ...newProducts]);
        setCurrentPage((prev) => prev + 1);

        // 如果返回的数据少于 PAGE_SIZE，说明没有更多数据了
        if (newProducts.length < PAGE_SIZE) {
          setHasMore(false);
        }
      } else {
        // 没有更多数据
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Failed to load more products:', error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, isLoadingMore, hasMore, PAGE_SIZE]);

  // 监听滚动到底部，加载下一页
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMoreProducts();
    }
  }, [inView]);

  // 商品点击处理
  const handleProductClick = useCallback(
    (productItem: any, index: number) => {
      trackProductClick(productItem.product_info, index, '商品版位');
    },
    [trackProductClick],
  );

  // 商品曝光处理
  const handleProductExposure = useCallback(
    (productItem: any, index: number) => {
      trackProductExposure(productItem.product_info, index, '商品版位');
    },
    [trackProductExposure],
  );

  if (!productList.length) {
    return null;
  }

  return (
    <div
      className={`w-full ${isWap ? 'px-[8px] py-[8px] bg-[#f4f4f4]' : 'px-[48px] pt-[16px] pb-[48px] bg-[#f1f2f5]'}`}
    >
      {/* PC端：网格布局 */}
      {!isWap && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-[16px]">
            {productList.map((productItem, index) => (
              <TrendProductItemPC
                key={`${productItem.product_info?.id || productItem.shop_sku}-${index}`}
                product={productItem.product_info}
                index={index}
                description={productItem.describe}
                onClick={() => handleProductClick(productItem, index)}
                onExposure={() => handleProductExposure(productItem, index)}
              />
            ))}
          </div>

          {/* 加载更多触发器 */}
          {hasMore && (
            <div ref={loadMoreRef} className="w-full py-[20px] flex items-center justify-center">
              {isLoadingMore && <div className="text-[14px] text-gray-500">Loading more...</div>}
            </div>
          )}
        </>
      )}

      {/* WAP端：瀑布流布局 */}
      {isWap && (
        <div ref={containerRef} className="w-full">
          {waterfallItemWidth && (
            <Waterfall<RecommendProductItem>
              data={productList}
              columns={2}
              gap={8}
              itemWidth={waterfallItemWidth}
              responsive={false}
              renderItem={(productItem: RecommendProductItem, index: number) => (
                <TrendProductItemMobile
                  key={`${productItem.product_info?.id || productItem.shop_sku}-${index}`}
                  product={productItem.product_info}
                  isFirstItem={index === 0}
                  description={productItem.describe}
                  onClick={() => handleProductClick(productItem, index)}
                  onExposure={() => handleProductExposure(productItem, index)}
                />
              )}
            />
          )}

          {/* 加载更多触发器 */}
          {hasMore && (
            <div ref={loadMoreRef} className="w-full py-[16px] flex items-center justify-center">
              {isLoadingMore && <div className="text-[12px] text-gray-500">{t('viewMore')}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
